import React, { useEffect, useRef, useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { cn } from '@/lib/utils';

export interface CarouselMediaItem {
  id?: string;
  url: string;
  media_type?: string | null;
}

interface MediaCarouselProps {
  media: CarouselMediaItem[];
  alt?: string;
  className?: string;
  containerClassName?: string;
  /** Use object-cover (admin grid thumb) instead of object-contain (feed) */
  objectCover?: boolean;
  /** Render image controls (prev/next arrows on desktop). Default true. */
  showArrows?: boolean;
  /** Optional callback when active slide changes */
  onSlideChange?: (index: number) => void;
  /** Max viewport height ratio for the carousel (default 0.75 = 75vh) */
  maxHeightVh?: number;
}

const isVideoType = (t?: string | null) => !!t && t.includes('video');

/**
 * Instagram-style media carousel: horizontal swipe, dots, counter, arrows.
 * Container height adapts dynamically to the active slide's aspect ratio so
 * mixed portrait/landscape media never leaves empty gray space below.
 */
const MediaCarousel: React.FC<MediaCarouselProps> = ({
  media,
  alt = 'Post media',
  className,
  containerClassName,
  objectCover = false,
  showArrows = true,
  onSlideChange,
  maxHeightVh = 0.75,
}) => {
  const single = media.length === 1;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    watchDrag: !single,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [ratios, setRatios] = useState<Record<number, number>>({}); // width / height
  const [containerWidth, setContainerWidth] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const idx = emblaApi.selectedScrollSnap();
    setSelectedIndex(idx);
    onSlideChange?.(idx);
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === idx && isVideoType(media[i]?.media_type)) {
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [emblaApi, media, onSlideChange]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Track wrapper width (for height = width / ratio)
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Recompute embla after a ratio is registered (slide height changed)
  useEffect(() => {
    emblaApi?.reInit();
  }, [ratios, containerWidth, emblaApi]);

  const setRatio = useCallback((idx: number, w: number, h: number) => {
    if (!w || !h) return;
    setRatios((prev) => (prev[idx] ? prev : { ...prev, [idx]: w / h }));
  }, []);

  if (!media || media.length === 0) return null;

  // `single` already declared above for embla options

  const maxHeightPx = typeof window !== 'undefined' ? window.innerHeight * maxHeightVh : 600;

  // Active slide drives the wrapper height (no extra gray space).
  const activeRatio = ratios[selectedIndex];
  const computedHeight =
    containerWidth && activeRatio
      ? Math.min(containerWidth / activeRatio, maxHeightPx)
      : undefined;

  return (
    <div
      ref={wrapperRef}
      className={cn('relative w-full bg-gray-100 transition-[height] duration-300 ease-out', containerClassName)}
      style={computedHeight ? { height: `${computedHeight}px` } : undefined}
    >
      <div ref={emblaRef} className="overflow-hidden w-full h-full">
        <div className="flex w-full h-full">
          {media.map((item, idx) => (
            <div
              key={item.id ?? idx}
              className="relative shrink-0 grow-0 basis-full w-full h-full flex items-center justify-center bg-gray-100"
            >
              {isVideoType(item.media_type) ? (
                <video
                  ref={(el) => (videoRefs.current[idx] = el)}
                  src={item.url}
                  controls
                  muted
                  playsInline
                  preload="metadata"
                  // @ts-ignore
                  webkit-playsinline="true"
                  onLoadedMetadata={(e) => {
                    const v = e.currentTarget;
                    setRatio(idx, v.videoWidth, v.videoHeight);
                  }}
                  className={cn(
                    'w-full h-full',
                    objectCover ? 'object-cover' : 'object-contain',
                    className
                  )}
                />
              ) : (
                <OptimizedImage
                  src={item.url}
                  alt={`${alt} ${idx + 1}`}
                  onLoad={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    setRatio(idx, img.naturalWidth, img.naturalHeight);
                  }}
                  className={cn(
                    'w-full h-full',
                    objectCover ? 'object-cover' : 'object-contain',
                    className
                  )}
                  priority={idx === 0}
                  sizes="(max-width: 768px) 100vw, 800px"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {!single && (
        <>
          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs font-medium px-2 py-0.5 rounded-full pointer-events-none z-10">
            {selectedIndex + 1}/{media.length}
          </div>

          {showArrows && (
            <>
              <button
                type="button"
                aria-label="Previous"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  emblaApi?.scrollPrev();
                }}
                className="hidden md:flex items-center justify-center absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 text-white z-10 disabled:opacity-30"
                disabled={selectedIndex === 0}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  emblaApi?.scrollNext();
                }}
                className="hidden md:flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 text-white z-10 disabled:opacity-30"
                disabled={selectedIndex === media.length - 1}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10 pointer-events-none">
            {media.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-all',
                  i === selectedIndex ? 'bg-white scale-110' : 'bg-white/50'
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaCarousel;
